import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION, EddYesNo } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types';
import { reportError, ObservabilityErrorSeverity } from '@Services/observability/initObservability';

export const useCharitiesLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'charities',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_CHARITIES]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_CHARITIES) || EddYesNo.NO,
      [EddEmbeddedFields.EDD_CHARITIES_DETAILS]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_CHARITIES_DETAILS) || '',
    },
    formSchema: z
      .object({
        [EddEmbeddedFields.EDD_CHARITIES]: z
          .string()
          .min(1, { message: tValidation('validation.required') }),
        [EddEmbeddedFields.EDD_CHARITIES_DETAILS]: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data[EddEmbeddedFields.EDD_CHARITIES] === EddYesNo.YES) {
          const details = data[EddEmbeddedFields.EDD_CHARITIES_DETAILS];
          if (!details || details.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.required'),
              path: [EddEmbeddedFields.EDD_CHARITIES_DETAILS],
            });
          } else if (details.length < 10) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.min10chars'),
              path: [EddEmbeddedFields.EDD_CHARITIES_DETAILS],
            });
          } else if (details.length > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.max100chars'),
              path: [EddEmbeddedFields.EDD_CHARITIES_DETAILS],
            });
          }
        }
      }),
  });

  const handleSubmit = async () => {
    const charitiesValue = formMethods.getValues(EddEmbeddedFields.EDD_CHARITIES);
    const detailsValue = formMethods.getValues(EddEmbeddedFields.EDD_CHARITIES_DETAILS);

    saveEddQuestionToLocalStore({
      question: t('organic.charities.title'),
      questionId: EddEmbeddedFields.EDD_CHARITIES,
      answer: JSON.stringify(charitiesValue),
    });

    if (charitiesValue === EddYesNo.YES && detailsValue && detailsValue.trim() !== '') {
      saveEddQuestionToLocalStore({
        question: t('organic.charities.detailsPrompt'),
        questionId: EddEmbeddedFields.EDD_CHARITIES_DETAILS,
        answer: JSON.stringify(detailsValue),
      });
    }

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_CHARITIES);
    } catch (error) {
      reportError({
        severity: ObservabilityErrorSeverity.ERROR,
        metadata: {
          name: 'EddCharitiesSubmitError',
          message: error instanceof Error ? error.message : String(error),
        },
        groupingHash: 'edd-submit-error',
      });
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
